import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { opsApi } from "@/operations/lib/api";

const BUCKETS = [["to", "To"], ["cc", "Cc"], ["bcc", "Bcc"]];

/** Global answer to who gets an automated email, written against roles. Each project inherits
 *  it and can still override person by person. */
export default function EmailDefaultsPanel({ templateKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await opsApi.emailDefaults()); }
    catch { setData({ defaults: {}, abs_roles: [], gc_roles: [], can_edit: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
  }

  const row = data.defaults?.[templateKey] || {};
  const picked = (bucket, side) => (row[bucket]?.[side] || []);

  const toggle = async (bucket, side, roleKey) => {
    const has = picked(bucket, side).includes(roleKey);
    const next = {
      ...data.defaults,
      [templateKey]: {
        ...BUCKETS.reduce((acc, [b]) => ({
          ...acc,
          [b]: { abs: [...picked(b, "abs")], gc: [...picked(b, "gc")] },
        }), {}),
      },
    };
    const target = next[templateKey][bucket];
    target[side] = has ? target[side].filter((r) => r !== roleKey) : [...target[side], roleKey];
    // A role belongs to one bucket only, so drop it from the other two.
    BUCKETS.filter(([b]) => b !== bucket).forEach(([b]) => {
      next[templateKey][b][side] = next[templateKey][b][side].filter((r) => r !== roleKey);
    });
    setBusy(true);
    try {
      setData(await opsApi.saveEmailDefaults(next));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that default");
    } finally { setBusy(false); }
  };

  const chips = (bucket, side, roles) => (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => {
        const on = picked(bucket, side).includes(r.key);
        return (
          <button key={r.key} disabled={!data.can_edit || busy}
                  onClick={() => toggle(bucket, side, r.key)}
                  data-testid={`email-default-${templateKey}-${bucket}-${side}-${r.key}`}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                    on ? "bg-blue-500 text-white shadow-sm"
                      : "border border-slate-200 text-slate-500 hover:border-slate-400"}`}>
            {r.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="mt-5 border-t border-slate-100 pt-4" data-testid="settings-email-defaults">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Who normally gets it
      </h4>
      <div className="mt-3 space-y-3">
        {BUCKETS.map(([bucket, label]) => (
          <div key={bucket} className="grid gap-2 sm:grid-cols-[3rem_1fr_1fr] sm:items-start">
            <span className="pt-0.5 text-xs font-semibold text-slate-700">{label}</span>
            <div>
              <span className="mb-1 block text-[10px] uppercase text-slate-400">ABS</span>
              {chips(bucket, "abs", data.abs_roles)}
            </div>
            <div>
              <span className="mb-1 block text-[10px] uppercase text-slate-400">GC</span>
              {chips(bucket, "gc", data.gc_roles)}
            </div>
          </div>
        ))}
      </div>
      {!data.can_edit && (
        <p className="mt-3 text-xs text-amber-700">
          Only an admin or accounting can change these.
        </p>
      )}
    </div>
  );
}
